import React, { useState, useEffect } from "react";
import {
    Layout,
    Menu,
    Button,
    List,
    Card,
    Modal,
    Input,
    Form,
    message,
    Spin,
    Badge,
    Empty,
    Drawer,
    Upload,
    AutoComplete
} from "antd";
import {
    InboxOutlined,
    SendOutlined,
    FileOutlined,
    DeleteOutlined,
    EditOutlined,
    ReloadOutlined,
    SaveOutlined,
    MenuOutlined,
    PaperClipOutlined,
    DownloadOutlined
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../configs";
import dayjs from "dayjs";
import "./Email.css";

const { Sider, Content } = Layout;
const { TextArea } = Input;

export default function Email() {
    const [selectedFolder, setSelectedFolder] = useState("INBOX");
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [composeVisible, setComposeVisible] = useState(false);
    const [replyMode, setReplyMode] = useState(false);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [fileList, setFileList] = useState([]);
    const [sending, setSending] = useState(false);
    const [userEmails, setUserEmails] = useState([]);
    const [folderStats, setFolderStats] = useState({});
    const [forwardMode, setForwardMode] = useState(false);
    const [emailSignature, setEmailSignature] = useState("");

    const [form] = Form.useForm();

    // Fetch emails
    const fetchEmails = async (folder = selectedFolder, page = 1) => {
        if (folder === "Signature") return; // Skip fetching emails for Signature view

        setLoading(true);
        console.log("[Email Frontend] Fetching emails from folder:", folder, "page:", page);

        try {
            // Drafts are stored in database, not IMAP
            if (folder === "INBOX.Drafts") {
                const res = await axiosInstance.get("/email/drafts");
                console.log("[Email Frontend] Drafts response:", res.data);

                const drafts = (res.data.data || []).map(draft => ({
                    ...draft,
                    messageId: draft.id_draft // Map id_draft to messageId for compatibility
                }));

                setEmails(drafts);
                setPagination({
                    ...pagination,
                    current: 1,
                    total: res.data.total || 0
                });
            } else {
                const res = await axiosInstance.get("/email/messages", {
                    params: { folder, page, limit: pagination.pageSize }
                });

                console.log("[Email Frontend] Response:", res.data);

                setEmails(res.data.data || []);
                setPagination({
                    ...pagination,
                    current: res.data.page,
                    total: res.data.total
                });
            }

            if ((emails.length === 0 || pagination.total === 0) && folder !== "INBOX.Drafts") {
                message.info(`Folder "${folder}" kosong atau tidak ada email`);
            }
        } catch (err) {
            console.error("[Email Frontend] Fetch emails error:", err);
            console.error("[Email Frontend] Error response:", err.response);

            let errorMessage = "Gagal memuat email";
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            }
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmails();
        fetchUserEmails();
        fetchFolderStats();
        fetchEmailSignature();

        // Poll folder stats every 30 seconds for real-time updates
        const interval = setInterval(fetchFolderStats, 30000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [selectedFolder]);

    // Fetch user emails for autocomplete
    const fetchUserEmails = async () => {
        try {
            const res = await axiosInstance.get("/email/user-emails");
            setUserEmails(res.data.emails || []);
        } catch (err) {
            console.error("Fetch user emails error:", err);
        }
    };

    // Fetch folder stats for badges
    const fetchFolderStats = async () => {
        try {
            const res = await axiosInstance.get("/email/folder-stats");
            setFolderStats(res.data.stats || {});
        } catch (err) {
            console.error("Fetch folder stats error:", err);
        }
    };

    // Fetch email signature
    const fetchEmailSignature = async () => {
        try {
            const res = await axiosInstance.get("/email/signature");
            setEmailSignature(res.data.signature || "");
        } catch (err) {
            console.error("Fetch email signature error:", err);
        }
    };

    // Handle folder change
    const handleFolderChange = (folder) => {
        setSelectedFolder(folder);
        setSelectedEmail(null);
    };

    // Handle email click
    const handleEmailClick = async (email) => {
        // If draft, open in compose modal
        if (selectedFolder === "INBOX.Drafts") {
            setReplyMode(false);
            setForwardMode(false);
            form.setFieldsValue({
                to: email.to,
                cc: email.cc,
                bcc: email.bcc,
                subject: email.subject,
                body: email.html || email.text
            });
            setComposeVisible(true);
            return;
        }

        // Show header data immediately, body loads in background
        setSelectedEmail({ ...email, _detailLoading: true });

        // Fetch full email detail in background (CID-to-base64 conversion & attachments)
        axiosInstance.get(`/email/message/${encodeURIComponent(email.messageId)}`, {
            params: { folder: selectedFolder }
        }).then(detailRes => {
            setSelectedEmail(prev => {
                if (prev?.messageId === email.messageId) {
                    return { ...detailRes.data, _detailLoading: false };
                }
                return prev;
            });
        }).catch(err => {
            console.error("[Email] Fetch detail error:", err);
            setSelectedEmail(prev => {
                if (prev?.messageId === email.messageId) {
                    return { ...prev, _detailLoading: false };
                }
                return prev;
            });
        });

        // Mark as read in database
        try {
            await axiosInstance.post("/email/mark-read", {
                messageId: email.messageId,
                folder: selectedFolder
            });
            console.log("[Email] Marked as read:", email.messageId);

            // Refresh folder stats to update badge
            fetchFolderStats();
        } catch (err) {
            console.error("[Email] Mark as read error:", err);
        }
        // Close sidebar on mobile
        if (window.innerWidth < 768) {
            setSidebarVisible(false);
        }
    };

    // Handle compose
    const handleCompose = () => {
        setReplyMode(false);
        setForwardMode(false);
        form.resetFields();
        setFileList([]);

        // Pre-fill with signature
        if (emailSignature) {
            form.setFieldsValue({
                body: `<br/><br/>${emailSignature}`
            });
        }

        setComposeVisible(true);
    };

    // Handle reply
    const handleReply = () => {
        if (!selectedEmail) return;

        setReplyMode(true);
        setForwardMode(false);

        const replyBody = `\n\n--- Original Message ---\nFrom: ${selectedEmail.from}\nDate: ${dayjs(selectedEmail.date).format("DD/MM/YYYY HH:mm")}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.text || ""}`;
        const bodyWithSignature = emailSignature ? `\n\n${emailSignature}${replyBody}` : replyBody;

        form.setFieldsValue({
            to: selectedEmail.from,
            subject: selectedEmail.subject.startsWith("Re:")
                ? selectedEmail.subject
                : `Re: ${selectedEmail.subject}`,
            body: bodyWithSignature
        });
        setComposeVisible(true);
    };

    // Handle forward
    const handleForward = () => {
        if (!selectedEmail) return;

        setForwardMode(true);
        setReplyMode(false);

        const forwardBody = `\n\n--- Forwarded Message ---\nFrom: ${selectedEmail.from}\nDate: ${dayjs(selectedEmail.date).format("DD/MM/YYYY HH:mm")}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.text || ""}`;
        const bodyWithSignature = emailSignature ? `\n\n${emailSignature}${forwardBody}` : forwardBody;

        form.setFieldsValue({
            subject: selectedEmail.subject.startsWith("Fwd:")
                ? selectedEmail.subject
                : `Fwd: ${selectedEmail.subject}`,
            body: bodyWithSignature
        });
        setComposeVisible(true);
    };

    // Handle save signature
    const handleSaveSignature = async () => {
        try {
            await axiosInstance.post("/email/signature", { signature: emailSignature });
            message.success("Signature updated successfully");
        } catch (err) {
            console.error("Save signature error:", err);
            message.error("Failed to update signature");
        }
    };

    // Handle send email
    const handleSend = async (values) => {
        setSending(true);
        try {
            const formData = new FormData();
            formData.append("to", values.to);
            if (values.cc) formData.append("cc", values.cc);
            if (values.bcc) formData.append("bcc", values.bcc);
            formData.append("subject", values.subject);
            formData.append("html", values.body);
            formData.append("text", values.body.replace(/<[^>]*>/g, ""));

            if (replyMode && selectedEmail?.messageId) {
                formData.append("messageId", selectedEmail.messageId);
            }

            // Append attachments
            fileList.forEach(file => {
                formData.append("attachments", file.originFileObj);
            });

            const endpoint = replyMode ? "/email/reply" : "/email/send";
            await axiosInstance.post(endpoint, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            message.success("Email berhasil dikirim");
            setComposeVisible(false);
            form.resetFields();
            setFileList([]);
            fetchEmails();
        } catch (err) {
            console.error("Send email error:", err);
            message.error(err.response?.data?.error || "Gagal mengirim email");
        } finally {
            setSending(false);
        }
    };

    // Handle save draft
    const handleSaveDraft = async () => {
        try {
            const values = form.getFieldsValue();

            await axiosInstance.post("/email/draft", {
                to: values.to,
                cc: values.cc,
                bcc: values.bcc,
                subject: values.subject,
                html: values.body,
                text: values.body ? values.body.replace(/<[^>]*>/g, "") : ""
            });

            message.success("Draft saved successfully");
            setComposeVisible(false);
            form.resetFields();
            setFileList([]);
        } catch (err) {
            console.error("Save draft error:", err);
            message.error("Failed to save draft");
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedEmail) return;

        Modal.confirm({
            title: selectedFolder === "INBOX.Drafts" ? "Hapus Draft" : "Hapus Email",
            content: selectedFolder === "INBOX.Drafts"
                ? "Apakah Anda yakin ingin menghapus draft ini secara permanen?"
                : "Apakah Anda yakin ingin menghapus email ini?",
            onOk: async () => {
                try {
                    if (selectedFolder === "INBOX.Drafts") {
                        // Delete draft logic
                        await axiosInstance.delete(`/email/draft/${selectedEmail.messageId}`);
                        message.success("Draft berhasil dihapus");
                    } else {
                        // Standard email delete logic
                        await axiosInstance.delete(`/email/delete/${selectedEmail.messageId}`, {
                            params: { folder: selectedFolder }
                        });
                        message.success("Email berhasil dihapus");
                    }

                    setSelectedEmail(null);
                    fetchEmails();
                    fetchFolderStats(); // Update badge count
                } catch (err) {
                    console.error("Delete error:", err);
                    message.error("Gagal menghapus");
                }
            }
        });
    };

    // Sidebar menu with badges
    const menuItems = [
        {
            key: "INBOX",
            icon: <InboxOutlined />,
            label: (
                <span>
                    Inbox
                    {folderStats.INBOX?.unread > 0 && (
                        <Badge
                            count={folderStats.INBOX.unread}
                            style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }}
                        />
                    )}
                    {folderStats.INBOX?.total > 0 && folderStats.INBOX?.unread === 0 && (
                        <Badge
                            count={folderStats.INBOX.total}
                            style={{ marginLeft: 8, backgroundColor: '#d9d9d9' }}
                        />
                    )}
                </span>
            )
        },
        {
            key: "INBOX.Sent",
            icon: <SendOutlined />,
            label: (
                <span>
                    Sent
                    {folderStats["INBOX.Sent"]?.total > 0 && (
                        <Badge
                            count={folderStats["INBOX.Sent"].total}
                            style={{ marginLeft: 8, backgroundColor: '#d9d9d9' }}
                        />
                    )}
                </span>
            )
        },
        {
            key: "INBOX.Drafts",
            icon: <FileOutlined />,
            label: (
                <span>
                    Drafts
                    {folderStats["INBOX.Drafts"]?.total > 0 && (
                        <Badge
                            count={folderStats["INBOX.Drafts"].total}
                            style={{ marginLeft: 8, backgroundColor: '#d9d9d9' }}
                        />
                    )}
                </span>
            )
        },
        {
            key: "INBOX.Trash",
            icon: <DeleteOutlined />,
            label: (
                <span>
                    Trash
                    {folderStats["INBOX.Trash"]?.total > 0 && (
                        <Badge
                            count={folderStats["INBOX.Trash"].total}
                            style={{ marginLeft: 8, backgroundColor: '#d9d9d9' }}
                        />
                    )}
                </span>
            )
        },
        {
            key: "Signature",
            icon: <EditOutlined />,
            label: "Signature"
        }
    ];

    const sidebarContent = (
        <div style={{ padding: "16px 0" }}>
            <Button
                type="primary"
                icon={<EditOutlined />}
                block
                size="large"
                onClick={handleCompose}
                style={{ margin: "0 16px 16px 16px", width: "calc(100% - 32px)" }}
            >
                Compose
            </Button>
            <Menu
                mode="inline"
                selectedKeys={[selectedFolder]}
                items={menuItems}
                onClick={({ key }) => handleFolderChange(key)}
            />
        </div>
    );

    return (
        <div className="email-container">
            <Layout style={{ minHeight: "calc(100vh - 200px)", background: "#fff" }}>
                {/* Desktop Sidebar */}
                <Sider
                    width={250}
                    style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}
                    className="email-sider-desktop"
                >
                    {sidebarContent}
                </Sider>

                {/* Mobile Drawer */}
                <Drawer
                    title="Email Folders"
                    placement="left"
                    onClose={() => setSidebarVisible(false)}
                    open={sidebarVisible}
                    className="email-drawer-mobile"
                >
                    {sidebarContent}
                </Drawer>

                <Layout>
                    {/* Email List */}
                    <Sider
                        width={350}
                        style={{ background: "#fafafa", borderRight: "1px solid #f0f0f0" }}
                        className="email-list-sider"
                    >
                        <div style={{ padding: 16, borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8 }}>
                            <Button
                                icon={<MenuOutlined />}
                                onClick={() => setSidebarVisible(true)}
                                className="mobile-menu-btn"
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchEmails()}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </div>

                        <Spin spinning={loading}>
                            <List
                                dataSource={emails}
                                locale={{ emptyText: <Empty description="Tidak ada email" /> }}
                                renderItem={(email) => (
                                    <List.Item
                                        onClick={() => handleEmailClick(email)}
                                        style={{
                                            cursor: "pointer",
                                            background: selectedEmail?.messageId === email.messageId ? "#e6f7ff" : "#fff",
                                            borderBottom: "1px solid #f0f0f0",
                                            padding: "12px 16px"
                                        }}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                                                        {email.from?.split("<")[0]?.trim() || email.from}
                                                    </span>
                                                    <span style={{ fontSize: 12, color: "#999" }}>
                                                        {dayjs(email.date).format("DD/MM")}
                                                    </span>
                                                </div>
                                            }
                                            description={
                                                <div>
                                                    <div style={{ fontWeight: 500, color: "#333", marginBottom: 4 }}>
                                                        {email.subject}
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: "#666",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        {email.text?.substring(0, 100)}
                                                    </div>
                                                    {email.hasAttachments && (
                                                        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                                                            <PaperClipOutlined style={{ marginRight: 4 }} />
                                                            Attachment
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Spin>
                    </Sider>

                    {/* Email Detail */}
                    <Content style={{ padding: 24, margin: 0, minHeight: 280, overflow: 'auto' }}>
                        {selectedFolder === "Signature" ? (
                            <Card title="Edit Email Signature">
                                <Form layout="vertical">
                                    <Form.Item label="Signature Content">
                                        <ReactQuill
                                            theme="snow"
                                            value={emailSignature}
                                            onChange={setEmailSignature}
                                            style={{ height: 300, marginBottom: 50 }}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                                    [{ 'font': [] }],
                                                    [{ 'size': ['small', false, 'large', 'huge'] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                    [{ 'script': 'sub' }, { 'script': 'super' }],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                                                    [{ 'align': [] }],
                                                    ['link', 'image', 'clean']
                                                ]
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item>
                                        <Button type="primary" onClick={handleSaveSignature} icon={<SaveOutlined />}>
                                            Save Signature
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        ) : selectedEmail ? (
                            <Card
                                title={
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <h2 style={{ margin: 0, fontSize: 20, wordBreak: "break-word" }}>{selectedEmail.subject}</h2>
                                            <div style={{ fontSize: 14, color: "#666", marginTop: 8 }}>
                                                <div><strong>From:</strong> {selectedEmail.from}</div>
                                                <div><strong>To:</strong> {selectedEmail.to}</div>
                                                <div><strong>Date:</strong> {dayjs(selectedEmail.date).format("DD/MM/YYYY HH:mm")}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <Button type="primary" icon={<SendOutlined />} onClick={handleReply}>
                                                Reply
                                            </Button>
                                            <Button icon={<SendOutlined />} onClick={handleForward}>
                                                Forward
                                            </Button>
                                            <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                }
                            >
                                {selectedEmail._detailLoading ? (
                                    <div style={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <Spin tip="Memuat konten email..." />
                                    </div>
                                ) : (
                                    <div
                                        style={{ minHeight: 300, lineHeight: 1.6 }}
                                        dangerouslySetInnerHTML={{ __html: selectedEmail.html || selectedEmail.text?.replace(/\n/g, "<br/>") }}
                                    />
                                )}

                                {/* Attachments Section */}
                                {Array.isArray(selectedEmail.attachments) && selectedEmail.attachments.length > 0 && (
                                    <div style={{
                                        marginTop: 16,
                                        padding: 16,
                                        background: '#fafafa',
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                                            <PaperClipOutlined style={{ marginRight: 6 }} />
                                            Attachment File :
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {selectedEmail.attachments.map((att, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{ cursor: 'pointer', color: '#1890ff', fontSize: 14 }}
                                                    onClick={async () => {
                                                        if (att.content) {
                                                            const byteChars = atob(att.content);
                                                            const byteNumbers = new Array(byteChars.length);
                                                            for (let i = 0; i < byteChars.length; i++) {
                                                                byteNumbers[i] = byteChars.charCodeAt(i);
                                                            }
                                                            const byteArray = new Uint8Array(byteNumbers);
                                                            const blob = new Blob([byteArray], { type: att.contentType || 'application/octet-stream' });
                                                            const url = URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = att.filename;
                                                            a.click();
                                                            URL.revokeObjectURL(url);
                                                        } else {
                                                            try {
                                                                const res = await axiosInstance.get(
                                                                    `/email/attachment/${encodeURIComponent(selectedEmail.messageId)}/${encodeURIComponent(att.filename)}`,
                                                                    { params: { folder: selectedFolder }, responseType: 'blob' }
                                                                );
                                                                const blob = new Blob([res.data], { type: att.contentType || 'application/octet-stream' });
                                                                const url = URL.createObjectURL(blob);
                                                                const a = document.createElement('a');
                                                                a.href = url;
                                                                a.download = att.filename;
                                                                a.click();
                                                                URL.revokeObjectURL(url);
                                                            } catch (err) {
                                                                console.error("Download attachment error:", err);
                                                                message.error("Gagal mengunduh lampiran");
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {idx + 1}. {att.filename} {att.size ? `(${(att.size / 1024).toFixed(1)} KB)` : ''}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ) : (
                            <Empty description="Pilih email untuk melihat detail" style={{ marginTop: 100 }} />
                        )}
                    </Content>
                </Layout>
            </Layout>

            {/* Compose Modal */}
            <Modal
                title={forwardMode ? "Forward Email" : (replyMode ? "Reply Email" : "Compose Email")}
                open={composeVisible}
                onCancel={() => {
                    setComposeVisible(false);
                    form.resetFields();
                    setFileList([]);
                }}
                width={800}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={handleSend}>
                    <Form.Item
                        label="To"
                        name="to"
                        rules={[{ required: true, message: "Please input recipient email" }]}
                    >
                        <AutoComplete
                            options={userEmails.map(email => ({ value: email }))}
                            placeholder="recipient@example.com"
                            filterOption={(inputValue, option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item label="CC" name="cc">
                        <AutoComplete
                            options={userEmails.map(email => ({ value: email }))}
                            placeholder="cc@example.com"
                            filterOption={(inputValue, option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item label="BCC" name="bcc">
                        <AutoComplete
                            options={userEmails.map(email => ({ value: email }))}
                            placeholder="bcc@example.com"
                            filterOption={(inputValue, option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="Subject"
                        name="subject"
                        rules={[{ required: true, message: "Please input subject" }]}
                    >
                        <Input placeholder="Email subject" />
                    </Form.Item>

                    <Form.Item
                        label="Message"
                        name="body"
                        rules={[{ required: true, message: "Please input message" }]}
                    >
                        <ReactQuill
                            theme="snow"
                            style={{ height: 350, marginBottom: 50 }}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                    [{ 'font': [] }],
                                    [{ 'size': ['small', false, 'large', 'huge'] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'color': [] }, { 'background': [] }],
                                    [{ 'script': 'sub' }, { 'script': 'super' }],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                                    [{ 'align': [] }],
                                    ['blockquote', 'code-block'],
                                    ['link', 'image'],
                                    ['clean']
                                ]
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Attachments">
                        <Upload
                            fileList={fileList}
                            onChange={({ fileList }) => setFileList(fileList)}
                            beforeUpload={() => false}
                            multiple
                            maxCount={10}
                        >
                            <Button icon={<FileOutlined />}>Attach Files (Max 10MB each)</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SendOutlined />}
                                loading={sending}
                                disabled={sending}
                            >
                                {sending ? "Sending..." : "Send"}
                            </Button>
                            <Button
                                icon={<FileOutlined />}
                                onClick={handleSaveDraft}
                                disabled={sending}
                            >
                                Save Draft
                            </Button>
                            <Button
                                onClick={() => {
                                    setComposeVisible(false);
                                    form.resetFields();
                                    setFileList([]);
                                }}
                                disabled={sending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
